import { Button, Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import {
  ANNOUNCEMENT_CATEGORIES,
  type AnnouncementInput,
  type AnnouncementRow,
  fromLocalDateTime,
  toLocalDateTime,
} from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/announcements`.
 *
 * docs/01-INFORMATION-ARCHITECTURE.md §5 renders these as the gold strip on the
 * home page, so the two fields worth more than the text are the pin and the
 * expiry. An unpinned notice is one nobody sees; a pinned one with no expiry is
 * one everybody sees for ever.
 */
export function AnnouncementsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams();

  const columns: Column<AnnouncementRow>[] = [
    {
      key: 'title',
      label: t('content.columns.title'),
      render: (row) => (
        <div className="min-w-48">
          <span className="font-semibold text-primary">{valueFor(row.title, locale)}</span>

          <span className="mt-1 flex flex-wrap gap-1.5">
            {row.is_pinned ? <Chip tone="navy">{t('content.announcements.pinned')}</Chip> : null}
            {row.is_featured ? (
              <Chip tone="gold">{t('content.announcements.featured')}</Chip>
            ) : null}
            {/* Expired, computed here because the row already carries the
                date — and an editor scanning the list is asking exactly this. */}
            {row.expires_at && new Date(row.expires_at) < new Date() ? (
              <Chip tone="gold">{t('content.announcements.expired')}</Chip>
            ) : null}
          </span>
        </div>
      ),
    },
    {
      key: 'category',
      label: t('content.columns.category'),
      render: (row) => (
        <span className="text-secondary">
          {t(`content.announcements.categories.${row.category}`)}
        </span>
      ),
    },
    {
      key: 'destination',
      label: t('content.announcements.destination'),
      render: (row) => {
        if (row.document) {
          return (
            <span className="text-caption text-secondary">
              {valueFor(row.document.title, locale)}
            </span>
          );
        }

        return row.external_url ? (
          <span className="truncate text-caption text-secondary">{row.external_url}</span>
        ) : (
          <span className="text-muted">{'—'}</span>
        );
      },
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'display_date',
      label: t('content.columns.date'),
      sortable: true,
      align: 'end',
      render: (row) => (
        <time dateTime={row.display_date} className="whitespace-nowrap text-secondary">
          {new Date(row.display_date).toLocaleDateString()}
        </time>
      ),
    },
  ];

  return (
    <ResourceListScreen
      resource="announcements"
      title={t('nav.items.announcements')}
      intro={t('content.announcements.intro')}
      columns={columns}
      labelFor={(row) => valueFor(row.title, locale)}
      emptyTitle={t('content.announcements.empty.title')}
      emptyBody={t('content.announcements.empty.body')}
      newLabel={t('content.announcements.new')}
      deleteTitle={t('content.announcements.delete.title')}
      deleteBody={(label) => t('content.delete.body', { label })}
      filters={
        <>
          <Select
            label={t('content.columns.category')}
            hideLabel
            value={String(list.params.category ?? '')}
            onChange={(event) => list.setFilter('category', event.target.value || null)}
            options={[
              { value: '', label: t('content.announcements.anyCategory') },
              ...ANNOUNCEMENT_CATEGORIES.map((category) => ({
                value: category,
                label: t(`content.announcements.categories.${category}`),
              })),
            ]}
          />

          {/* §4's scheduled task archives past the expiry; until it runs, or
              when it has failed, this is how somebody finds the notice about a
              deadline three weeks gone. */}
          <Button
            type="button"
            size="sm"
            variant={list.params.expired ? 'primary' : 'secondary'}
            aria-pressed={Boolean(list.params.expired)}
            onClick={() => list.setFilter('expired', list.params.expired ? null : '1')}
          >
            {t('content.announcements.expired')}
          </Button>
        </>
      }
    />
  );
}

interface Fields {
  title: TranslatedValue;
  body: TranslatedValue;
  display_date: string;
  category: (typeof ANNOUNCEMENT_CATEGORIES)[number];
  icon: string;
  document_id: string;
  external_url: string;
  is_pinned: boolean;
  is_featured: boolean;
  expires_at: string;
  sort_order: number;
  status: AnnouncementRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();

  const mayPublish = can('announcements.publish');

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<AnnouncementRow, Fields, AnnouncementInput>({
      resource: 'announcements',
      recordId,
      empty,
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'title',
        'body',
        'display_date',
        'category',
        'icon',
        'document_id',
        'external_url',
        'expires_at',
        'sort_order',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('content.announcements.new') : t('content.announcements.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/announcements')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('content.fields.title')}
            name="title"
            value={values.title}
            onChange={(next) => setValue('title', next, { shouldDirty: true })}
            error={formState.errors.title?.message}
          />

          <RichTextField
            label={t('content.fields.body')}
            value={values.body}
            onChange={(next) => setValue('body', next, { shouldDirty: true })}
            error={formState.errors.body?.message}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              {...register('display_date')}
              type="date"
              label={t('content.announcements.fields.displayDate')}
              hint={t('content.announcements.fields.displayDateHint')}
              error={formState.errors.display_date?.message}
            />

            <Select
              label={t('content.columns.category')}
              hint={t('content.announcements.fields.categoryHint')}
              {...register('category')}
              options={ANNOUNCEMENT_CATEGORIES.map((category) => ({
                value: category,
                label: t(`content.announcements.categories.${category}`),
              }))}
            />

            <Input
              {...register('expires_at')}
              type="datetime-local"
              label={t('content.announcements.fields.expiresAt')}
              hint={t('content.announcements.fields.expiresAtHint')}
              error={formState.errors.expires_at?.message}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('content.fields.order')}
              hint={t('content.fields.orderHint')}
            />
          </div>

          {/* §5's card is a single link, and the server refuses both at once —
              so the pair is presented as the choice it is. */}
          <fieldset className="space-y-4 rounded-card border border-subtle p-4">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('content.announcements.destination')}
            </legend>

            <p className="text-caption text-muted">{t('content.announcements.destinationHint')}</p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                {...register('document_id')}
                type="number"
                min={1}
                label={t('content.announcements.fields.documentId')}
                hint={t('content.announcements.fields.documentIdHint')}
                error={formState.errors.document_id?.message}
              />

              <Input
                {...register('external_url')}
                type="url"
                label={t('content.announcements.fields.externalUrl')}
                error={formState.errors.external_url?.message}
              />
            </div>

            {record?.document ? (
              <p className="text-caption text-secondary">
                {t('content.announcements.linkedDocument', {
                  title: valueFor(record.document.title, 'ka'),
                })}
              </p>
            ) : null}
          </fieldset>

          <label className="flex items-center gap-2 text-body-sm text-primary">
            <input
              type="checkbox"
              {...register('is_pinned')}
              className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            />
            {t('content.announcements.fields.pinned')}
          </label>
          <p className="-mt-3 text-caption text-muted">
            {t('content.announcements.fields.pinnedHint')}
          </p>

          {/*
           * §1.3's hero card. Separate from `is_pinned` on purpose: an
           * admissions deadline belongs on the hero *and* in §3.2's strip, an
           * exam timetable usually only in the strip, and one checkbox could
           * not say both.
           */}
          <label className="flex items-center gap-2 text-body-sm text-primary">
            <input
              type="checkbox"
              {...register('is_featured')}
              className="h-4 w-4 rounded border-strong text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            />
            {t('content.announcements.fields.featured')}
          </label>
          <p className="-mt-3 text-caption text-muted">
            {t('content.announcements.fields.featuredHint')}
          </p>
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="announcements"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              title: { label: t('content.fields.title'), value: values.title },
              body: { label: t('content.fields.body'), value: values.body },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    title: {},
    body: {},
    // Today, because an announcement is nearly always about now — and an empty
    // required date field is one more thing to fill in for no reason.
    display_date: new Date().toISOString().slice(0, 10),
    category: 'general',
    icon: '',
    document_id: '',
    external_url: '',
    is_pinned: false,
    is_featured: false,
    expires_at: '',
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: AnnouncementRow): Fields {
  return {
    title: row.title,
    body: row.body,
    display_date: row.display_date,
    category: row.category,
    icon: row.icon ?? '',
    // Strings in the form so an emptied number input is `''` rather than the
    // `NaN` a `valueAsNumber` register produces.
    document_id: row.document_id === null ? '' : String(row.document_id),
    external_url: row.external_url ?? '',
    is_pinned: row.is_pinned,
    is_featured: row.is_featured,
    expires_at: toLocalDateTime(row.expires_at),
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): AnnouncementInput {
  return {
    title: form.title,
    body: form.body,
    display_date: form.display_date,
    category: form.category,
    icon: form.icon.trim() === '' ? null : form.icon.trim(),
    document_id: form.document_id === '' ? null : Number(form.document_id),
    external_url: form.external_url.trim() === '' ? null : form.external_url.trim(),
    is_pinned: form.is_pinned,
    is_featured: form.is_featured,
    expires_at: fromLocalDateTime(form.expires_at),
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    // §3's "Submit for review", same rule as news and documents: saving
    // without the permission *is* submitting, and a publisher's save carries
    // the flag through rather than answering it.
    pending_review: mayPublish ? form.pending_review : true,
  };
}
